export enum AssetTypes {
  Agent = "agent",
  App = "app",
  Canvas = "canvas",
  Chatbot = "chatbot",
  Connection = "connection",
  Folder = "folder",
  Interface = "interface",
  NextGenForm = "nextGenForm",
  NextGenZap = "nextGenZap",
  Table = "table",
  TableView = "tableView",
  TableSubscription = "tableSubscription",
  Template = "template",
  Zap = "zap",
}

export enum ProjectActions {
  ImportAsset = "importAsset",
  ImportCanvas = "importCanvas",
  ManageAccess = "manageAccess",
  MoveProjectAsset = "moveProjectAsset",
  None = "none",
  Rename = "rename",
  Delete = "delete",
}

export enum AccessType {
  Viewer = "viewer",
  Owner = "owner",
  Editor = "editor",
}

export interface ZapType {
  title: string;
  id?: string | number;
}

export enum VirtualFolders {
  Personal = "personal",
  Trash = "trash",
}

export enum ApprovalStatus {
  Approved = "APPROVED",
  Cancelled = "CANCELLED",
  Denied = "DENIED",
  Pending = "PENDING",
}

export enum ApprovalType {
  Delete = "DELETE",
  Publish = "PUBLISH",
  TurnOff = "TURN_OFF",
  TurnOn = "TURN_ON",
}

export enum ApprovalRequiredOperation {
  Delete = "delete",
  Toggle = "toggle",
  TurnOff = "turn_off",
}

/**
 * Creates a union of types that share a common discriminating property.
 *
 * @example
 * ```ts
 * type HydratedSubject = TaggedUnion<
 *   "subjectType",
 *   {
 *     user: { id: string; fullName: string; email: string }
 *     zap: { id: string; title: string }
 *   }
 * >;
 *
 * // The resulting type will be the same as:
 * type HydratedSubject =
 *   | { subjectType: "user"; id: string; fullName: string; email: string }
 *   | { subjectType: "zap"; id: string; title: string }
 * ```
 */
export type TaggedUnion<
  TypeIdentifier extends string,
  Types extends Record<string, unknown>,
> = {
  [Name in keyof Types]: Record<TypeIdentifier, Name> & Types[Name];
}[keyof Types];

/**
 * Extends a base type given a discriminating property and a map of additional details.
 *
 * @example
 * ```ts
 * type Base = { id: string; subjectType: "user" | "zap"; };
 *
 * type DetailMap = {
 *   user: { email: string; fullName: string };
 *   zap: { title: string; description: string };
 * };
 *
 * type HydratedSubject = DiscriminatedMerge<Base, "subjectType", DetailMap>;
 *
 * // The resulting type will be the same as:
 * type HydratedSubject =
 *   | { subjectType: "user"; id: string; email: string; fullName: string }
 *   | { subjectType: "zap"; id: string; title: string; description: string }
 * ```
 */
export type DiscriminatedMerge<
  Base,
  TypeIdentifier extends keyof Base & string,
  DetailMap extends Record<keyof DetailMap, unknown>,
> = Base & TaggedUnion<TypeIdentifier, DetailMap>;
